import React, { useState } from "react";
import { api } from "../../lib/api";
import { STATUS_OPTIONS } from "./adminConfig";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

const HIDDEN = ["id", "created_at", "updated_at", "key", "sort"];

function isBilingual(v) { return v && typeof v === "object" && ("fr" in v || "en" in v); }

export default function RecordEditor({ collection, record, onSaved, onCancel }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(record)));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBiling = (k, lang, v) => setForm((f) => ({ ...f, [k]: { ...f[k], [lang]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      HIDDEN.forEach((h) => h !== "key" && delete payload[h]);
      if (record.id) {
        await api.put(`/admin/${collection}/${record.id}`, payload);
      } else {
        await api.post(`/admin/${collection}`, payload);
      }
      toast.success("Enregistré");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const keys = Object.keys(form).filter((k) => !HIDDEN.includes(k));

  return (
    <div className="space-y-4" data-testid="record-editor">
      {keys.map((k) => {
        const v = form[k];
        if (k === "status" && collection === "services") {
          return (
            <div key={k} className="space-y-1">
              <Label className="capitalize">{k}</Label>
              <select value={v} onChange={(e) => setField(k, e.target.value)}
                data-testid={`field-${k}`} className="h-11 w-full rounded-lg border border-input bg-white px-3">
                {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          );
        }
        if (typeof v === "boolean") {
          return (
            <div key={k} className="flex items-center justify-between rounded-lg border border-input p-3">
              <Label className="capitalize">{k.replace(/_/g, " ")}</Label>
              <Switch checked={v} onCheckedChange={(x) => setField(k, x)} data-testid={`field-${k}`} />
            </div>
          );
        }
        if (isBilingual(v)) {
          const long = ["desc", "bio", "quote", "a", "purpose"].includes(k);
          const Comp = long ? Textarea : Input;
          return (
            <div key={k} className="space-y-1">
              <Label className="capitalize">{k.replace(/_/g, " ")}</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                <Comp placeholder="FR" value={v.fr || ""} onChange={(e) => setBiling(k, "fr", e.target.value)} data-testid={`field-${k}-fr`} />
                <Comp placeholder="EN" value={v.en || ""} onChange={(e) => setBiling(k, "en", e.target.value)} data-testid={`field-${k}-en`} />
              </div>
            </div>
          );
        }
        return (
          <div key={k} className="space-y-1">
            <Label className="capitalize">{k.replace(/_/g, " ")}</Label>
            <Input value={v ?? ""} onChange={(e) => setField(k, e.target.value)} data-testid={`field-${k}`} />
          </div>
        );
      })}
      <div className="flex gap-2 pt-2">
        <Button onClick={save} disabled={saving} data-testid="record-save" className="rounded-full bg-[#1E293B] hover:bg-[#0f172a]">
          {saving ? "…" : "Enregistrer"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full" data-testid="record-cancel">Annuler</Button>
      </div>
    </div>
  );
}
